import type {
  AppointmentDocumentationUiSnapshot,
  AppointmentDraftGenerationUi,
} from "./appointment-documentation-state";

export type AppointmentWorkspaceTab = "note" | "draft";

/**
 * The Enhanced SOAP draft tab stays locked until an Enhancement exists to
 * show — running, failed, or landed. A locked tab explains itself with a
 * tooltip; it never opens an empty surface.
 */
export function isDraftTabLocked(generation: AppointmentDraftGenerationUi): boolean {
  return generation.phase === "idle";
}

/**
 * Product call (2026-08-18): Enhance switches to the draft tab immediately.
 * The switch happens once, on the idle -> anything edge. That edge is
 * crossed when Enhance starts and also on first load of an Appointment
 * whose draft already exists (the store starts at idle before its first
 * snapshot), so a returning RD lands on the draft. Later polls never move
 * the RD again.
 */
export function opensDraftTab(
  previous: AppointmentDraftGenerationUi["phase"],
  next: AppointmentDraftGenerationUi["phase"]
): boolean {
  return previous === "idle" && next !== "idle";
}

/**
 * What the Appointment note footer says about Enhance, derived from the
 * durable documentation snapshot. One state at a time, in the order the RD
 * experiences them: no transcript yet, transcript trouble, ready, running,
 * landed. Copy for each lives in the footer component.
 */
export type EnhanceFooterState =
  | { kind: "checking" }
  | { kind: "waiting" }
  | { kind: "transcript_failed" }
  | { kind: "transcript_conflict" }
  | { kind: "unavailable" }
  | { kind: "retry" }
  | { kind: "ready"; requesting: boolean }
  | { kind: "enhancing"; writing: boolean }
  | { kind: "generation_failed"; retryable: boolean }
  | { kind: "draft_ready" }
  /** NO-785: a Re-enhance runs; Running, then Writing, like Enhance. */
  | { kind: "reenhancing"; writing: boolean }
  /** NO-785: the latest Re-enhance failed or was cancelled; the draft is unchanged. */
  | { kind: "reenhance_failed" };

/**
 * NO-785: what a drafted Session's footer says. A Draft that was ever approved
 * reads plain draft_ready; then a running Re-enhance; then one that failed or
 * was cancelled; then draft_ready. With `soap-re-enhance` off it is always
 * draft_ready.
 */
function draftedFooterState(
  ui: AppointmentDocumentationUiSnapshot,
  reEnhanceEnabled: boolean
): EnhanceFooterState {
  const reEnhancement = ui.reEnhancement;

  if (!reEnhanceEnabled || !reEnhancement || reEnhancement.draftEverApproved) {
    return { kind: "draft_ready" };
  }

  if (reEnhancement.state === "running") {
    return { kind: "reenhancing", writing: reEnhancement.writing };
  }

  return reEnhancement.state === null
    ? { kind: "draft_ready" }
    : { kind: "reenhance_failed" };
}

export function enhanceFooterState(
  ui: AppointmentDocumentationUiSnapshot,
  reEnhanceEnabled = false
): EnhanceFooterState {
  const { snapshot, enhanceAction, generation } = ui;
  if (!snapshot) return { kind: "checking" };

  if (enhanceAction === "unavailable") return { kind: "unavailable" };

  if (enhanceAction === "requesting") return { kind: "ready", requesting: true };

  if (snapshot.transcriptState === "failed") return { kind: "transcript_failed" };

  if (snapshot.transcriptState === "conflict") return { kind: "transcript_conflict" };

  if (snapshot.transcriptState !== "ready") return { kind: "waiting" };

  if (enhanceAction === "retry") return { kind: "retry" };

  switch (generation.phase) {
    case "preparing":
      return { kind: "enhancing", writing: generation.writing };
    case "draft_ready":
      return draftedFooterState(ui, reEnhanceEnabled);
    case "failed":
    case "cancelled":
      return { kind: "generation_failed", retryable: enhanceAction === "available" };
    case "idle":
      return { kind: "ready", requesting: false };
  }
}

/**
 * NO-785: the Draft editor's identity. It changes only when a Re-enhance
 * starts or stops running: failed, cancelled, and none all read settled, so
 * the RD's own saves and an approval never remount the editor. Each change
 * reloads the head: read-only while running, then the new draft after success
 * or the unchanged, editable draft after a failure.
 */
export function draftEditorKey(ui: AppointmentDocumentationUiSnapshot): string {
  const phase = ui.reEnhancement?.state === "running" ? "running" : "settled";

  return `${ui.snapshot?.enhancementJobId ?? "none"}:${phase}`;
}

/**
 * NO-785: the server's re-enhance route needs both `soap-enhancement` and
 * `soap-re-enhance`, so the workspace offers Re-enhance only when both are on.
 */
export function reEnhanceEnabled(flags: {
  soapEnhancement: boolean;
  soapReEnhance: boolean;
}): boolean {
  return flags.soapEnhancement && flags.soapReEnhance;
}

/**
 * NO-428: the ready status tells the RD Enhance can wait until they are done
 * only when `soap-enhancement` and `soap-auto-enhance` are both on, because
 * only then will the worker start it after they leave.
 */
export function autoEnhanceHintEnabled(flags: {
  soapEnhancement: boolean;
  soapAutoEnhance: boolean;
}): boolean {
  return flags.soapEnhancement && flags.soapAutoEnhance;
}

/** NO-785: the Re-enhance action exists only for a Draft that was never approved. */
export function reEnhanceOffered(
  enabled: boolean,
  reEnhancement: AppointmentDocumentationUiSnapshot["reEnhancement"]
): boolean {
  return enabled && reEnhancement?.draftEverApproved !== true;
}

/**
 * NO-785: what the Draft editor allows. An approved draft's body is locked
 * until "Make changes"; a running Re-enhance locks the body and Approve
 * whatever the review state, without claiming the draft is approved. A
 * disabled editor emits no edits, and autosave only ever saves an edit.
 */
export function draftBodyControls(input: {
  reviewState: "draft" | "approved" | undefined;
  unlocked: boolean;
  readOnly: boolean;
}): { editorDisabled: boolean; showsApprovedLock: boolean; approveBlocked: boolean } {
  const approvedLock = input.reviewState === "approved" && !input.unlocked;

  return {
    editorDisabled: approvedLock || input.readOnly,
    showsApprovedLock: approvedLock && !input.readOnly,
    approveBlocked: input.readOnly,
  };
}

/**
 * NO-785: the revision a Re-enhance names is the one the Draft editor has
 * loaded, not the polled head: a tab still showing an older revision after
 * another tab saved must get draft_changed, never replace what it hasn't
 * shown. Before the editor has loaded anything, nothing is on screen to be
 * stale, so the polled head stands in.
 */
export function reEnhanceRevision(
  shownRevisionId: string | null,
  generation: AppointmentDraftGenerationUi
): string | null {
  if (shownRevisionId !== null) return shownRevisionId;

  return generation.phase === "draft_ready" ? generation.draftRevisionId : null;
}
